/**
 * Network Health Card Component
 *
 * Compact card showing network health overview.
 * Opens a modal with detailed PartnerNetworkProgress when clicked.
 * Includes edit functionality for managing partner trade preferences.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Activity, TrendingUp, Settings2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PartnerNetworkProgress } from '@/components/PartnerNetworkProgress';
import EditPartnerNetworkModal from '@/components/EditPartnerNetworkModal';

interface PartnerNetworkProgressData {
  userProfileId: string;
  totalCategories: number;
  filledCategories: number;
  progressPercentage: number;
  hasPreferences: boolean;
}

interface NetworkHealthCardProps {
  userId?: string;
  partnerConnections?: number;
}

export default function NetworkHealthCard({
  userId,
  partnerConnections = 0,
}: NetworkHealthCardProps) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Deliberately React Query on NetworkPage's key rather than a bare useEffect fetch: the
  // endpoint is then requested once instead of twice, and — load-bearing for the `?category=`
  // deep link — this card's load state becomes observable to that page, which waits for the
  // query to hold data before scrolling. The card sits directly above the partner list and
  // its skeleton is much shorter than its loaded state, so a fetch the page cannot see is a
  // fetch that silently drags the scroll target down after we have already scrolled to it.
  const { data: progressData, isPending } = useQuery<PartnerNetworkProgressData>({
    queryKey: ['/api/workflows/partner-network-progress'],
    queryFn: () =>
      api.get<PartnerNetworkProgressData>('/api/workflows/partner-network-progress'),
    // Matches the freshness of the useEffect this replaced, which refetched on every mount;
    // the client defaults (staleTime 5min, refetchOnMount false) would have made the card
    // staler than it was.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  if (isPending) {
    return (
      <Card className="shadow-md border border-gray-200 mb-6">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-4 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!progressData) {
    return null;
  }

  const { totalCategories, filledCategories, progressPercentage, hasPreferences } = progressData;

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    // Refreshes NetworkPage's copy of the same query too, which the old local fetch left stale.
    queryClient.invalidateQueries({ queryKey: ['/api/workflows/partner-network-progress'] });
  };

  // Show setup prompt if user hasn't set preferences yet
  if (!hasPreferences) {
    return (
      <>
        <Card className="shadow-md border border-amber-200 bg-amber-50 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Set Up Your Partner Network
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Select the trades you want to find partners for. Choose at least 12 trades to
                  build a diverse network that can help with your referrals.
                </p>
                <Button onClick={() => setIsEditModalOpen(true)}>
                  <Settings2 className="w-4 h-4 mr-2" />
                  Set Up Network
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <EditPartnerNetworkModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          userProfileId={progressData.userProfileId}
          onSuccess={handleEditSuccess}
        />
      </>
    );
  }

  const isComplete = progressPercentage >= 100;

  return (
    <>
      <Card
        className={`shadow-md hover:shadow-lg transition-all duration-300 mb-6 cursor-pointer group ${
          isComplete
            ? 'border-4 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50 ring-4 ring-green-300/60'
            : 'border border-gray-200'
        }`}
        onClick={() => setIsModalOpen(true)}
      >
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-800">
                Network Health
              </h3>
              {isComplete && (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Complete
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEditClick}
                className="text-gray-500 hover:text-indigo-600"
                title="Edit partner trades"
              >
                <Settings2 className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1 text-indigo-600 group-hover:text-indigo-700 transition-colors">
                <span className="text-sm font-medium">View Details</span>
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Main Metric */}
          <div className="mb-3">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-gray-900">
                {Math.round(progressPercentage)}%
              </span>
              <span className="text-lg text-gray-500">coverage</span>
            </div>
          </div>

          {/* Sub Stats */}
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>
              {filledCategories} of {totalCategories} trades covered
            </span>
            <span className="text-gray-400">•</span>
            <span>
              {partnerConnections} connected partner{partnerConnections !== 1 ? 's' : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Modal with Full Details */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-800">
              Network Health Details
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <PartnerNetworkProgress
              userId={userId}
              compact={false}
              showCategoryDetails={true}
              partnerConnections={partnerConnections}
              onNavigate={() => setIsModalOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Partner Network Modal */}
      <EditPartnerNetworkModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        userProfileId={progressData.userProfileId}
        onSuccess={handleEditSuccess}
      />
    </>
  );
}
